// Localização: frontend/src/components/Pacientes/FilterPaciente.jsx
// Componente flutuante para filtrar e navegar entre pacientes com scroll do mouse
// Exibe apenas as fotos dos pacientes alinhadas horizontalmente com efeito de carrossel infinito

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { usePacientes } from '../../hooks/usePacientes';
import { getPacienteFotoUrl } from '../../utils/imageUtils';

const PACIENTES_VISIVEIS = 5;
const FALLBACK_AVATAR = '/images/higeia-avatar-shield.png';

/**
 * @component FilterPaciente
 * @description Componente flutuante que exibe apenas fotos de pacientes com efeito de carrossel infinito
 * @param {function} onPacienteSelect - Callback quando um paciente é selecionado
 * @param {function} onClose - Callback para fechar o componente
 * @param {function} onClearSelection - Callback para limpar a seleção do paciente
 * @param {object} selectedPaciente - Paciente atualmente selecionado (opcional)
 * @param {string} position - Posição do componente ('top-left', 'top-right', 'top-center', 'bottom-left', 'bottom-right')
 */
const FilterPaciente = ({ 
    onPacienteSelect, 
    onClose,
    onClearSelection,
    selectedPaciente = null,
    position = 'top-center' 
}) => {
    const { pacientes, isLoading, fetchPacientes } = usePacientes();
    const [startIndex, setStartIndex] = useState(0);
    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [direction, setDirection] = useState('forward'); // 'forward' ou 'backward'
    const containerRef = useRef(null);
    const scrollTimeoutRef = useRef(null);

    // Sincronizar paciente selecionado com prop externa
    useEffect(() => {
        if (selectedPaciente) {
            setPacienteSelecionado(selectedPaciente.id);
        } else {
            setPacienteSelecionado(null);
        }
    }, [selectedPaciente]);

    // Buscar pacientes ao montar o componente
    useEffect(() => {
        fetchPacientes();
    }, [fetchPacientes]);

    // Filtrar apenas pacientes ativos
    const pacientesAtivos = pacientes.filter(paciente => {
        const statusLower = paciente.status?.toLowerCase() || '';
        return statusLower !== 'excluído' && statusLower !== 'excluido';
    });

    // Criar lista circular para efeito infinito
    const criarListaCircular = useCallback(() => {
        if (pacientesAtivos.length === 0) return [];
        
        // Duplicar a lista para criar efeito infinito
        const listaDuplicada = [...pacientesAtivos, ...pacientesAtivos];
        
        // Obter os pacientes visíveis a partir do índice atual
        const pacientesVisiveis = [];
        for (let i = 0; i < PACIENTES_VISIVEIS; i++) {
            const index = (startIndex + i) % pacientesAtivos.length;
            pacientesVisiveis.push({
                ...pacientesAtivos[index],
                originalIndex: index,
                displayIndex: i
            });
        }
        
        return pacientesVisiveis;
    }, [pacientesAtivos, startIndex]);

    const pacientesVisiveis = criarListaCircular();

    // Função para avançar (próximo paciente aparece pela direita, primeiro some pela esquerda)
    const avancar = useCallback(() => {
        if (isAnimating || pacientesAtivos.length === 0) return;
        
        setIsAnimating(true);
        setDirection('forward');
        setStartIndex(prev => (prev + 1) % pacientesAtivos.length);
        
        setTimeout(() => {
            setIsAnimating(false);
        }, 300);
    }, [isAnimating, pacientesAtivos.length]);

    // Função para retroceder (anterior aparece pela esquerda, último some pela direita)
    const retroceder = useCallback(() => {
        if (isAnimating || pacientesAtivos.length === 0) return;
        
        setIsAnimating(true);
        setDirection('backward');
        setStartIndex(prev => (prev - 1 + pacientesAtivos.length) % pacientesAtivos.length);
        
        setTimeout(() => {
            setIsAnimating(false);
        }, 300);
    }, [isAnimating, pacientesAtivos.length]);

    // Detectar scroll do mouse no componente
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleWheel = (e) => {
            // Prevenir scroll padrão da página
            e.preventDefault();
            e.stopPropagation();

            // Limpar timeout anterior
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            // Debounce para evitar múltiplas mudanças rápidas
            scrollTimeoutRef.current = setTimeout(() => {
                if (e.deltaY > 0) {
                    // Scroll para baixo = avançar (próximo aparece)
                    avancar();
                } else if (e.deltaY < 0) {
                    // Scroll para cima = retroceder (anterior aparece)
                    retroceder();
                }
            }, 150);
        };

        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [avancar, retroceder]);

    // Função para selecionar um paciente
    const handlePacienteClick = (paciente) => {
        // Se já está selecionado, não faz nada (evita desselecionar ao clicar novamente)
        if (pacienteSelecionado === paciente.id) {
            return;
        }
        setPacienteSelecionado(paciente.id);
        if (onPacienteSelect) {
            onPacienteSelect(paciente);
        }
    };

    // Função para limpar a seleção
    const handleClearSelection = (e) => {
        e.stopPropagation();
        setPacienteSelecionado(null);
        if (onClearSelection) {
            onClearSelection();
        }
    };

    // Determinar classes de posição
    const getPositionClasses = () => {
        const positions = {
            'top-left': 'top-20 left-4',
            'top-right': 'top-20 right-4',
            'top-center': 'top-20 left-1/2 -translate-x-1/2',
            'bottom-left': 'bottom-4 left-4',
            'bottom-right': 'bottom-4 right-4'
        };
        return positions[position] || positions['top-center'];
    };

    // Não renderizar nada se estiver carregando ou não houver pacientes
    if (isLoading || pacientesAtivos.length === 0) {
        return null;
    }

    return (
        <div 
            ref={containerRef}
            className={`fixed ${getPositionClasses()} z-50 flex items-center gap-3 px-4 py-2 overflow-visible`}
        >
            {pacientesVisiveis.map((paciente, index) => {
                const isSelected = pacienteSelecionado === paciente.id;
                const isFirst = index === 0;
                const isLast = index === PACIENTES_VISIVEIS - 1;
                
                // Determinar animação baseada na direção do scroll
                const getAnimationClass = () => {
                    if (!isAnimating) return '';
                    
                    if (direction === 'forward') {
                        // Avançando: primeiro sai pela esquerda, novo entra pela direita
                        if (isFirst) return 'animate-slide-out-left';
                        if (isLast) return 'animate-slide-in-right';
                    } else {
                        // Retrocedendo: último sai pela direita, novo entra pela esquerda
                        if (isLast) return 'animate-slide-out-right';
                        if (isFirst) return 'animate-slide-in-left';
                    }
                    
                    return '';
                };
                
                return (
                    <div
                        key={`${paciente.id}-${paciente.originalIndex}-${startIndex}`}
                        onClick={() => handlePacienteClick(paciente)}
                        className={`
                            relative cursor-pointer transition-all duration-300
                            ${getAnimationClass()}
                        `}
                        title={paciente.nome_completo || 'Paciente'}
                    >
                        {/* Foto do paciente */}
                        <div className="relative group">
                            <img
                                className={`w-16 h-16 rounded-full object-cover border-2 transition-all duration-300 group-hover:scale-125 group-hover:shadow-xl ${
                                    isSelected 
                                        ? 'border-emerald-500 shadow-lg ring-2 ring-emerald-300' 
                                        : 'border-white shadow-md group-hover:border-emerald-300'
                                }`}
                                src={getPacienteFotoUrl(paciente.fotoURL)}
                                alt={`Foto de ${paciente.nome_completo || 'Paciente'}`}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = FALLBACK_AVATAR;
                                }}
                            />
                            {/* Botão X para fechar seleção - aparece apenas quando selecionado */}
                            {isSelected && (
                                <button
                                    onClick={handleClearSelection}
                                    className="absolute -top-1 -right-1 z-20 p-1 bg-red-500 hover:bg-red-600 rounded-full shadow-lg transition-all duration-200 hover:scale-110 flex items-center justify-center"
                                    title="Remover filtro de paciente"
                                    aria-label="Remover filtro de paciente"
                                >
                                    <X className="h-3 w-3 text-white" />
                                </button>
                            )}
                            {/* Status badge */}
                            <div 
                                className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full border-2 border-white z-10 transition-transform duration-300 group-hover:scale-110 ${
                                    paciente.status === 'Ativo' ? 'bg-green-500' : 'bg-red-500'
                                }`}
                                title={paciente.status}
                            />
                        </div>
                    </div>
                );
            })}
            
            {/* Estilos de animação inline */}
            <style>{`
                @keyframes slideOutLeft {
                    from {
                        opacity: 1;
                        transform: translateX(0) scale(1);
                    }
                    to {
                        opacity: 0;
                        transform: translateX(-100px) scale(0.7);
                    }
                }
                
                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(100px) scale(0.7);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0) scale(1);
                    }
                }
                
                @keyframes slideOutRight {
                    from {
                        opacity: 1;
                        transform: translateX(0) scale(1);
                    }
                    to {
                        opacity: 0;
                        transform: translateX(100px) scale(0.7);
                    }
                }
                
                @keyframes slideInLeft {
                    from {
                        opacity: 0;
                        transform: translateX(-100px) scale(0.7);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0) scale(1);
                    }
                }
                
                .animate-slide-out-left {
                    animation: slideOutLeft 0.3s ease-out forwards;
                }
                
                .animate-slide-in-right {
                    animation: slideInRight 0.3s ease-out forwards;
                }
                
                .animate-slide-out-right {
                    animation: slideOutRight 0.3s ease-out forwards;
                }
                
                .animate-slide-in-left {
                    animation: slideInLeft 0.3s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default FilterPaciente;

